import { useState } from "react";
import { toast } from "react-toastify";
import { markAttendance } from "../../context/actions/attendance";

function formatTime(isoString) {
  if (!isoString) return "";
  return new Date(isoString).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDate(isoString) {
  if (!isoString) return "";
  return new Date(isoString).toLocaleDateString([], {
    weekday: "short", month: "short", day: "numeric",
  });
}

export default function ClassAttendanceCard({ classData, onAttended }) {
  const [attended, setAttended]     = useState(classData?.attended ?? false);
  const [submitting, setSubmitting] = useState(false);

  if (!classData) return null;

  const {
    id, topic, status, start_time, end_time, link,
    course_name, materials, type,
    attendance_window_open,
  } = classData;
  const materialList = materials || [];

  const isLive     = status === "live";
  const isUpcoming = status === "upcoming";
  const isMakeup   = type === "makeup";

  const handleMarkAttendance = async (silent = false) => {
    if (submitting || attended) return;
    setSubmitting(true);
    const res = await markAttendance(id);
    setSubmitting(false);
    if (res.ok) {
      setAttended(true);
      if (onAttended) onAttended();
      toast.success("Attendance marked! +10 points 🎉");
    } else if (!silent) {
      toast.error(res.message);
    }
    // silent failures (e.g. attendance window not open yet) are swallowed —
    // the student still joined the class, they just fall back to the
    // explicit "Mark Attendance" button below.
  };

  const handleDownload = (material) => {
    window.open(material.url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Header bar */}
      <div
        className={`px-4 py-3 flex items-center justify-between ${
          isLive
            ? "bg-gradient-to-r from-red-600 to-rose-500"
            : "bg-gradient-to-r from-[#294a93] to-[#3b60c4]"
        }`}
      >
        <div className="flex items-center gap-2">
          {isLive && (
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse" />
            </span>
          )}
          <span className="text-white text-sm font-semibold">
            {isMakeup
              ? (isLive ? "🔁 Makeup Class · Live Now" : "🔁 Upcoming Makeup Class")
              : (isLive ? "🔴 Live Now" : "📅 Upcoming Class")}
          </span>
        </div>
        <span className="text-xs bg-white/20 text-white px-2 py-1 rounded-lg">
          {formatDate(start_time)} · {formatTime(start_time)} – {formatTime(end_time)}
        </span>
      </div>

      <div className="p-4">
        {/* Course badge */}
        {course_name && (
          <span className="inline-block text-xs bg-blue-50 text-[#294a93] font-medium px-2 py-0.5 rounded-full mb-2">
            {course_name}
          </span>
        )}

        {/* Topic */}
        <p className="text-sm font-semibold text-gray-800 mb-4 leading-snug">
          {topic || "Class Session"}
        </p>

        {/* Actions row */}
        <div className="flex flex-wrap gap-2">
          {/* Join class link — also silently marks attendance (skipped for makeup classes) */}
          {link && (isLive || isMakeup) && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => !isMakeup && handleMarkAttendance(true)}
              className="flex-1 text-center py-2 px-3 rounded-xl bg-[#294a93] text-white text-xs font-semibold hover:bg-[#1e3a7a] transition-colors"
            >
              Join Class
            </a>
          )}

          {/* Attendance button */}
          {!isMakeup && isLive && attendance_window_open && (
            attended ? (
              <div className="flex-1 text-center py-2 px-3 rounded-xl bg-emerald-50 text-emerald-700 text-xs font-semibold border border-emerald-200">
                ✓ Attendance Marked
              </div>
            ) : (
              <button
                onClick={handleMarkAttendance}
                disabled={submitting}
                className="flex-1 py-2 px-3 rounded-xl bg-[#d15424] text-white text-xs font-semibold hover:bg-[#b8451d] disabled:opacity-50 transition-colors"
              >
                {submitting ? "Marking…" : "Mark Attendance"}
              </button>
            )
          )}

          {/* Download materials */}
          {materialList.map((m) => (
            <button
              key={m.id}
              onClick={() => handleDownload(m)}
              className="flex items-center gap-1.5 py-2 px-3 rounded-xl border border-gray-200 text-gray-600 text-xs font-medium hover:border-[#294a93] hover:text-[#294a93] transition-colors"
            >
              <span>📄</span>
              {m.name || "Class Material"}
            </button>
          ))}
        </div>

        {/* Upcoming hint */}
        {!isMakeup && isUpcoming && (
          <p className="mt-3 text-xs text-gray-400 text-center">
            Attendance opens when class starts and closes after <strong>120 minutes</strong>.
          </p>
        )}

        {/* Live but window not open yet */}
        {!isMakeup && isLive && !attendance_window_open && !attended && (
          <p className="mt-3 text-xs text-gray-400 text-center">
            Attendance window opens at class start time and is available for 120 minutes.
          </p>
        )}

        {/* Makeup class hint */}
        {isMakeup && (
          <p className="mt-3 text-xs text-gray-400 text-center">
            This is a makeup class — attendance isn't tracked, just join in.
          </p>
        )}
      </div>
    </div>
  );
}
